/**
 * QueueSection — Phase 2 sidebar widget (Feb 2026).
 *
 * Hits GET /api/annotations/queue to surface the current user's open
 * bookmarks + assignments. Each row links to its matter with a clause
 * deep-link (matches the Herald link format the backend uses):
 *
 *   /chat/{matter_id}?clause={clause_id}
 *
 * Count badge is shown on the section heading when the list is
 * non-empty. Quiet (collapsed) when empty.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Bookmark, UserPlus, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchQueue } from './annotationsApi';

const TYPE_ICON = {
  comment: MessageSquare,
  highlight: MessageSquare,
  bookmark: Bookmark,
  assignment: UserPlus,
};

const truncate = (text, n = 60) => {
  if (!text) return '';
  const t = String(text);
  return t.length <= n ? t : t.slice(0, n - 1) + '…';
};

export const QueueSection = ({ accessToken, isArabic, initialItems }) => {
  const [items, setItems] = useState(Array.isArray(initialItems) ? initialItems : []);
  const [loaded, setLoaded] = useState(Array.isArray(initialItems));

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    try {
      const list = await fetchQueue({ accessToken });
      setItems(Array.isArray(list) ? list : []);
    } catch {
      // Best-effort — sidebar must never break on a failure here.
      setItems([]);
    } finally {
      setLoaded(true);
    }
  }, [accessToken]);

  useEffect(() => {
    if (Array.isArray(initialItems)) return;
    refresh();
    const onChanged = () => refresh();
    window.addEventListener('bayenah:annotation-created', onChanged);
    window.addEventListener('bayenah:annotation-resolved', onChanged);
    return () => {
      window.removeEventListener('bayenah:annotation-created', onChanged);
      window.removeEventListener('bayenah:annotation-resolved', onChanged);
    };
  }, [refresh, initialItems]);

  // Cluster B-1, Q4-a (Feb 06 2026): always-visible queue with empty
  // state — discoverability beats space-saving for a feature most
  // users won't notice on their own. The header + badge render
  // unconditionally; the body switches to an instructional empty
  // state when items is empty.
  return (
    <div
      className="px-3 pb-3 pt-3"
      data-testid="sidebar-queue-section"
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <span
          className={cn(
            'text-[11px] uppercase tracking-wider text-white/40',
            isArabic && 'font-arabic'
          )}
        >
          {isArabic ? 'قائمة المتابعة' : 'Queue'}
        </span>
        <span
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
            items.length > 0 ? 'bg-white text-black' : 'bg-white/[0.08] text-white/40'
          )}
          data-testid="sidebar-queue-badge"
        >
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div
          className={cn(
            'text-[11px] leading-snug text-white/40 px-1',
            isArabic && 'font-arabic text-right'
          )}
          data-testid="sidebar-queue-empty"
        >
          {isArabic
            ? 'لا توجد عناصر بعد — حدد نصاً في الفقرة ثم اختر "حفظ للمتابعة".'
            : 'No items yet — select clause text and choose Save for later.'}
        </div>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 10).map((it) => {
            const Icon = TYPE_ICON[it.type] || MessageSquare;
            const href = `/chat/${it.matter_id}?clause=${it.clause_id}`;
            return (
              <li key={it.id} data-testid={`sidebar-queue-item-${it.id}`}>
                <a
                  href={href}
                  className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-white/[0.06] transition-colors text-white/80 hover:text-white"
                  title={it.body || ''}
                  data-testid={`annotation-activity-item-${it.id}`}
                >
                  <Icon className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-white/60" />
                  <div className="flex-1 min-w-0">
                    <div className={cn('text-xs leading-snug', isArabic && 'font-arabic')}>
                      {truncate(it.body || it.quote || it.clause_id, 60)}
                    </div>
                    <div className="text-[10px] text-white/40 truncate flex items-center gap-1">
                      <span data-testid={`annotation-activity-item-${it.id}-clause`}>
                        {it.clause_id}
                      </span>
                      {(it.author_name || it.author_email || it.created_by) && (
                        <>
                          <span aria-hidden>·</span>
                          <span data-testid={`annotation-activity-item-${it.id}-author`}>
                            {it.author_name || it.author_email || it.created_by}
                          </span>
                        </>
                      )}
                      {it.created_at && (
                        <>
                          <span aria-hidden>·</span>
                          <span
                            data-testid={`annotation-activity-item-${it.id}-ts`}
                            title={it.created_at}
                          >
                            {new Date(it.created_at).toLocaleString(
                              isArabic ? 'ar' : 'en',
                              { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' },
                            )}
                          </span>
                        </>
                      )}
                    </div>
                    {it._carry_forward_source_id && (
                      <div className="mt-1">
                        <span
                          data-testid={`annotation-carry-forward-chip-${it.id}`}
                          title={it._carry_forward_kind === 'fuzzy_quote'
                            ? (isArabic ? 'مطابقة تقريبية' : 'fuzzy match')
                            : (isArabic ? 'مطابقة دقيقة' : 'exact match')}
                          className={cn(
                            'inline-block px-1.5 py-0 rounded-full text-[9px] uppercase tracking-wider',
                            isArabic && 'font-arabic',
                          )}
                          style={{
                            background: 'rgba(180,140,60,0.18)',
                            color: '#d6b27d',
                            border: '1px solid rgba(180,140,60,0.35)',
                          }}
                        >
                          {isArabic ? 'منقولة من المسودة السابقة' : 'Carried forward from v1'}
                        </span>
                      </div>
                    )}
                  </div>
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default QueueSection;
